// src/domains/broadcast.store.ts —— 推广推送切片 s.bc：状态形状、草稿 ↔ 契约互转、提交前本地校验（纯函数，服务端同口径复核）
import type { Dict, Lang } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { todayIn } from '../../../shared/src/time';
import type {
  BcBotOption,
  BcButton,
  BcCampaign,
  BcCampaignInput,
  BcDelivery,
  BcEstimate,
  BcFilterCard,
  BcFilterLang,
  BcQuota,
  BcRun,
  BcSchedule,
  BcScheduleKind,
  BcStatus,
} from '../broadcast-contract';
import type { AgentState } from '../state';
import { DATE_RE, MAX_BOTS, MAX_BUTTONS, TIME_RE, TITLE_MAX, checkButtonUrl, countWithFooter, textLimit, utcToWall, wallToUtc, type UrlCheck } from './broadcast.lib';
import { str, type AgentCtx } from './types';

export type BcView = 'list' | 'edit' | 'detail' | 'run';
// 失败明细页签：只用契约内的 status / fail_kind 取值（「其他」类失败在「全部失败」页签按原因列区分）
export type DlTab = 'failed' | 'blocked' | 'not_found' | 'skipped';
export const DL_TABS: readonly DlTab[] = ['failed', 'blocked', 'not_found', 'skipped'];

export interface BcDraft {
  id: number | null; // null = 尚未保存
  version: number;
  status: BcStatus | null;
  title: string;
  botIds: number[];
  filterCard: BcFilterCard;
  filterLang: BcFilterLang;
  textZh: string;
  textEn: string;
  imageId: number | null;
  buttons: BcButton[];
  kind: BcScheduleKind;
  runAt: string; // once：平台时区墙上时间 'YYYY-MM-DDTHH:MM'
  timeOfDay: string; // daily / weekly：HH:MM
  weekdays: number[]; // weekly：1..7
  endDate: string; // YYYY-MM-DD 或 ''
  tz: string; // 展示与换算所用时区（编辑取活动 timezone，新建取 config.timezone）
  previewLang: Lang;
  dirty: boolean;
}

export interface BcConfirm {
  kind: 'cancel' | 'submit';
  id: number;
  lines: readonly string[];
}

export interface BcSlice {
  view: BcView;
  quota: BcQuota | null;
  loadErr: string; // 配额加载失败（含接口未就绪）：整页显示错误 + 重试
  bots: readonly BcBotOption[];
  list: readonly BcCampaign[];
  total: number;
  page: number;
  status: '' | BcStatus;
  cur: BcCampaign | null;
  runs: readonly BcRun[];
  runsTotal: number;
  runsPage: number;
  run: BcRun | null;
  dl: readonly BcDelivery[];
  dlTotal: number;
  dlPage: number;
  dlTab: DlTab;
  draft: BcDraft | null;
  estimate: BcEstimate | null;
  uploading: boolean;
  formErr: string;
  confirm: BcConfirm | null;
  thumbs: Readonly<Record<number, string>>; // 图片 id → object URL（带 Bearer 经 blob 取图，离开页面统一 revoke）
}

declare module '../state' {
  interface AgentSlices {
    bc?: BcSlice;
  }
}

export const LIST_SIZE = 10;
export const RUNS_SIZE = 10;
export const DL_SIZE = 20;

export function freshBc(): BcSlice {
  return {
    view: 'list',
    quota: null,
    loadErr: '',
    bots: [],
    list: [],
    total: 0,
    page: 1,
    status: '',
    cur: null,
    runs: [],
    runsTotal: 0,
    runsPage: 1,
    run: null,
    dl: [],
    dlTotal: 0,
    dlPage: 1,
    dlTab: 'failed',
    draft: null,
    estimate: null,
    uploading: false,
    formErr: '',
    confirm: null,
    thumbs: {},
  };
}

const BC0: BcSlice = freshBc();

export function bcOf(s: AgentState): BcSlice {
  return s.bc ?? BC0;
}

export function setBc(ctx: AgentCtx, patch: Partial<BcSlice>): void {
  ctx.host.setState({ bc: { ...bcOf(ctx.host.state), ...patch } });
}

export function setDraft(ctx: AgentCtx, patch: Partial<BcDraft>, opts: { keepEstimate?: boolean } = {}): void {
  const cur = bcOf(ctx.host.state).draft;
  if (!cur) return;
  setBc(ctx, { draft: { ...cur, ...patch, dirty: true }, formErr: '', ...(opts.keepEstimate ? {} : { estimate: null }) });
}

export function platformTz(s: AgentState): string {
  return s.sh.config?.timezone || 'UTC';
}

export function emptyDraft(s: AgentState): BcDraft {
  const bots = bcOf(s).bots;
  return {
    id: null,
    version: 0,
    status: null,
    title: '',
    botIds: bots.length === 1 && bots[0] ? [bots[0].id] : [],
    filterCard: 'any',
    filterLang: 'any',
    textZh: '',
    textEn: '',
    imageId: null,
    buttons: [],
    kind: 'now',
    runAt: '',
    timeOfDay: '10:00',
    weekdays: [],
    endDate: '',
    tz: platformTz(s),
    previewLang: 'zh',
    dirty: false,
  };
}

export function draftOf(c: BcCampaign, fallbackTz: string): BcDraft {
  const tz = c.timezone || fallbackTz;
  const sc = c.schedule;
  return {
    id: c.id,
    version: c.version,
    status: c.status,
    title: c.title,
    botIds: [...c.bot_ids],
    filterCard: c.filter_card,
    filterLang: c.filter_lang,
    textZh: c.text_zh,
    textEn: c.text_en ?? '',
    imageId: c.image_file_id,
    buttons: c.buttons.map((b) => ({ ...b, url: b.url ?? '' })),
    kind: sc.kind,
    runAt: sc.kind === 'once' ? utcToWall(sc.run_at, tz) : '',
    timeOfDay: sc.time_of_day ?? '10:00',
    weekdays: [...(sc.weekdays ?? [])],
    endDate: sc.end_date ?? '',
    tz,
    previewLang: 'zh',
    dirty: false,
  };
}

function scheduleOf(d: BcDraft): BcSchedule {
  if (d.kind === 'now') return { kind: 'now' };
  if (d.kind === 'once') return { kind: 'once', run_at: wallToUtc(d.runAt, d.tz) ?? '' };
  const end = d.endDate ? { end_date: d.endDate } : {};
  if (d.kind === 'daily') return { kind: 'daily', time_of_day: d.timeOfDay, ...end };
  return { kind: 'weekly', time_of_day: d.timeOfDay, weekdays: [...d.weekdays].sort((a, b) => a - b), ...end };
}

// 契约 BcCampaignInput：webapp 按钮省略 url；英文文案 / 英文按钮文字原样（空 = 回落中文，由后端处理）
export function inputOf(d: BcDraft): BcCampaignInput {
  return {
    title: d.title.trim(),
    bot_ids: [...d.botIds],
    filter_card: d.filterCard,
    filter_lang: d.filterLang,
    text_zh: d.textZh,
    text_en: d.textEn,
    image_file_id: d.imageId,
    buttons: d.buttons.map((b) => (b.kind === 'webapp' ? { kind: 'webapp', label_zh: b.label_zh.trim(), label_en: b.label_en.trim() } : { kind: 'url', label_zh: b.label_zh.trim(), label_en: b.label_en.trim(), url: (b.url ?? '').trim() })),
    schedule: scheduleOf(d),
  };
}

// 中文文案计数：英文文案为空时英文用户收到中文文案 + 英文尾注（更长），按两者较大者计（计数器与校验同一口径）
export function zhCount(d: Pick<BcDraft, 'textZh' | 'textEn'>): number {
  const zh = countWithFooter(d.textZh, 'zh');
  return d.textEn.trim() ? zh : Math.max(zh, countWithFooter(d.textZh, 'en'));
}

const URL_WHY:Readonly<Record<Exclude<UrlCheck, 'ok'>, string>> = { format: 'bcUrlFormat', scheme: 'bcUrlScheme', userinfo: 'bcUrlUserinfo', tme: 'bcUrlTme' };

// 本地预检：返回首条错误文案，空串 = 通过（服务端为准，4220 另行映射）
export function validateDraft(t: Dict, d: BcDraft, now: Date = new Date()): string {
  const title = d.title.trim();
  if (!title || title.length > TITLE_MAX) return str(t, 'bcErrTitle');
  if (d.botIds.length < 1 || d.botIds.length > MAX_BOTS) return str(t, 'bcErrBots');
  if (!d.textZh.trim()) return str(t, 'bcErrTextZh');
  const max = textLimit(d.imageId !== null);
  const counts: ReadonlyArray<readonly [Lang, number]> = [['zh', zhCount(d)], ['en', d.textEn.trim() ? countWithFooter(d.textEn, 'en') : 0]];
  for (const [lang, n] of counts) {
    if (n > max) return interpolate(str(t, 'bcErrTooLong'), { lang: str(t, lang === 'zh' ? 'bcLangNameZh' : 'bcLangNameEn'), n, max });
  }
  if (d.buttons.length > MAX_BUTTONS) return str(t, 'bcFButtons');
  for (let k = 0; k < d.buttons.length; k++) {
    const b = d.buttons[k];
    if (!b) continue;
    if (!b.label_zh.trim()) return interpolate(str(t, 'bcErrBtnLabel'), { i: k + 1 });
    if (b.kind === 'url') {
      const c = checkButtonUrl(b.url ?? '');
      if (c !== 'ok') return interpolate(str(t, 'bcErrBtnUrl'), { i: k + 1, why: str(t, URL_WHY[c]) });
    }
  }
  if (d.kind === 'once') {
    const at = wallToUtc(d.runAt, d.tz);
    if (!at || new Date(at).getTime() <= now.getTime()) return str(t, 'bcErrRunAt');
  }
  if (d.kind === 'daily' || d.kind === 'weekly') {
    if (!TIME_RE.test(d.timeOfDay)) return str(t, 'bcErrTime');
    if (d.kind === 'weekly' && d.weekdays.length === 0) return str(t, 'bcErrWeekdays');
    if (d.endDate && (!DATE_RE.test(d.endDate) || d.endDate < todayIn(d.tz, now))) return str(t, 'bcErrEndDate');
  }
  return '';
}

// 可编辑状态（规格状态机：draft / rejected 可编辑再 submit；scheduled 编辑→draft 须重新 submit）
export function editable(st: BcStatus): boolean {
  return st === 'draft' || st === 'rejected' || st === 'scheduled';
}

export function submittable(st: BcStatus): boolean {
  return st === 'draft' || st === 'rejected';
}

export function cancellable(st: BcStatus): boolean {
  return st !== 'done' && st !== 'cancelled';
}

export function runActive(st: BcRun['status']): boolean {
  return st === 'queued' || st === 'snapshotting' || st === 'sending' || st === 'paused';
}
